import { track as gtmTrack } from './adapters/google_tag_manager.js'
import { track as segmentTrack } from './adapters/segment.js'
import { track as uaTrack } from './adapters/universal_analytics.js'

const SESSION_STORAGE_KEY = 'provenance_visitor_signals_tracked'
const FIRST_PRODUCT_PAGE_IN_SESSION_VIEWED_SAMPLE_RATE = 0.1 // 10%

// Fallback when sessionStorage is unavailable
let visitorSignalsTracked = false

/**
 * Gets raw visitor signals for analytics.
 * These can be analyzed in SQL to detect AI referrals/agents.
 * @returns {{ url: string, referrer: string | null, user_agent: string }}
 */
export function getVisitorSignals () {
  let referrerHostname = null
  try {
    if (document.referrer) {
      referrerHostname = new URL(document.referrer).hostname
    }
  } catch {
    // Invalid referrer URL, leave as null
  }

  return {
    url: window.location.href,
    referrer: referrerHostname,
    user_agent: navigator.userAgent
  }
}

// Track analytics. Default to sending to both Provenance and client analytics
export function track (eventData, roles = ['provenance', 'client']) {
  if (window.provenance && window.provenance.disableAnalytics) {
    console.debug('Provenance: Analytics tracking call ignored')
    return
  }

  roles.forEach((role) => {
    switch (role) {
      case 'client': gtmTrack(eventData.ga); break
      case 'provenance': uaTrack(eventData.ga); break
    }
  })
  if (eventData.segment) {
    segmentTrack(eventData.segment)
  }
}

/**
 * Tracks product page visitor signals.
 * Only fires once per browser session (survives page refresh/navigation).
 * Falls back to in-memory tracking if sessionStorage is unavailable.
 * Sampled at FIRST_PRODUCT_PAGE_IN_SESSION_VIEWED_SAMPLE_RATE.
 */
export function trackProductPageVisitorSignals () {
  if (hasTrackedThisSession()) return

  // we are interested only in the first product page in session, no need to track this for every page
  // because the agents activity will be noticed by the first visit
  markTrackedThisSession()

  // random sampling to keep the data volume down
  if (Math.random() > FIRST_PRODUCT_PAGE_IN_SESSION_VIEWED_SAMPLE_RATE) return

  const signals = getVisitorSignals()

  segmentTrack({
    object: 'productPage',
    action: 'viewedFirstInSession',
    sampled_at_rate: FIRST_PRODUCT_PAGE_IN_SESSION_VIEWED_SAMPLE_RATE,
    ...signals
  })
}

function hasTrackedThisSession () {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true'
  } catch {
    return visitorSignalsTracked
  }
}

function markTrackedThisSession () {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, 'true')
  } catch {
    visitorSignalsTracked = true
  }
}

export function listenForImpressions (container, detectedCallback) {
  const observer = createObserver(detectedCallback)

  observer.observe(container)
}

function createObserver (detectedCallback) {
  const options = {
    threshold: 0.5
  }

  return new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        if (!observer) {
          console.log('Observer is undefined or null')
        } else {
          observer.disconnect()
        }
        detectedCallback()
      }
    })
  }, options)
}
